// DevHub: remember where you were on a page, and start new pages at the top.
//
// Two problems this fixes, both long-standing:
//   • Pressing Back from a doc dropped you at the top of a long listing, so you had to scroll all the
//     way down to where you were. Nothing restored scroll — browsers do that for the DOCUMENT, and in
//     this app the document does not scroll (below).
//   • Navigating INTO a page kept the previous page's offset, because the scroll container is rendered
//     by <Root> and never remounts across routes. Opening a doc from halfway down /docs landed you
//     halfway down the doc.
//
// WHICH ELEMENT SCROLLS is the load-bearing detail. ODX's core theme pins the app shell to the
// viewport (`.odx-app-root{height:100dvh}`, `overflow:hidden`) and odx-overrides.css makes
// `main.odx-app-main` the scroll container, so `window.scrollY` is always ~0 inside the internal hub.
// The public/partner world has no such <main> — PublicShell.css deliberately lets the document scroll
// — so the selector below resolves to the document there. One helper, both worlds.
//
// react-router's own <ScrollRestoration> is unavailable: it requires createBrowserRouter, and
// Backstage wires a plain BrowserRouter.
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'devhub:scroll:';
// How long to keep re-applying a restored offset while lazy route chunks and their data land. The
// listing pages are React.lazy and fetch after mount, so on the first frames the content is too short
// for the saved offset and the browser clamps it — a single useEffect would restore to 0.
//
// A DEADLINE in milliseconds, not a frame count: frames only tick while the tab is visible and the old
// 90-frame budget was ~1.5s at 60fps, which a slow catalog fetch on staging can outlast — the retry
// loop would then give up before the list existed and the restore would silently no-op.
const RESTORE_DEADLINE_MS = 5000;

/** The element that actually scrolls: the internal shell's <main>, else the document. */
function getScroller(): Element | null {
  return document.querySelector('main.odx-app-main') ?? document.scrollingElement;
}

// Keyed by PATHNAME, not by history key. Filter changes rewrite the URL with `replace`, which mints a
// new history key on every keystroke — keying by that would spray a sessionStorage entry per character
// and lose the offset behind whichever key Back happened to land on. "Where I was on /docs" is both
// what people mean and naturally bounded by the number of pages visited.
function storageKey(pathname: string): string {
  return `${STORAGE_PREFIX}${pathname}`;
}

/**
 * Save/restore the scroll offset per page. Restores on a Back/Forward (POP) navigation, resets to the
 * top on a new (PUSH) navigation, and leaves REPLACE alone — filter and tab changes replace the entry,
 * and yanking the page to the top mid-typing would be hostile.
 *
 * Mount ONCE, above the routes (see AppBody in App.tsx).
 */
export function useScrollRestoration(): void {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  // The last offset seen by a scroll event. Read at cleanup instead of the live `scrollTop`, because by
  // then the incoming route's (possibly shorter) content can already have clamped it to 0.
  const lastTopRef = useRef(0);

  // ── Track the current page's offset ──
  //
  // The scroll container is resolved INSIDE the handler, and the listener is attached to `document` in
  // the CAPTURE phase. Both details are load-bearing:
  //
  //   • Scroll events do not bubble, so a document-level listener only sees them during capture.
  //   • The container must not be captured once when the effect runs. This hook mounts above the role
  //     gate, which renders a splash while the user's role resolves — so on every hard load the first
  //     run happens before `main.odx-app-main` exists. getScroller() then fell back to the document,
  //     and because the effect's only dep is `pathname` it never re-ran once the real shell mounted.
  //     The document does not scroll in the internal hub (scrollHeight === clientHeight), so the tracked
  //     offset stayed 0 forever, every write stored "0", and the restore below discarded it as `<= 0`.
  //     That is why Back never restored scroll on /docs. It appeared to work on /catalog only because
  //     that path REDIRECTS to /catalog-cards, and the pathname change re-ran the effect after the
  //     shell had mounted — masking the bug on the one page the e2e suite covered.
  useEffect(() => {
    const key = storageKey(pathname);
    lastTopRef.current = getScroller()?.scrollTop ?? 0;
    // Only flush on unmount if a scroll was actually observed. Otherwise a run that never saw the real
    // container would overwrite a good stored offset with 0 on the next hard reload.
    let sawScroll = false;

    let frame = 0;
    const onScroll = (event: Event) => {
      const scroller = getScroller();
      if (!scroller) return;
      // Ignore scrolls from nested scrollers (a dropdown, the TechDocs reader): the document reports
      // itself as `document`, an element reports the element.
      if (event.target !== scroller && event.target !== document) return;
      lastTopRef.current = scroller.scrollTop;
      sawScroll = true;
      if (frame) return; // already scheduled — coalesce the burst into one write per frame
      frame = requestAnimationFrame(() => {
        frame = 0;
        try {
          sessionStorage.setItem(key, String(lastTopRef.current));
        } catch {
          // Private mode / quota — remembering scroll is a nicety, never a failure.
        }
      });
    };

    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (frame) cancelAnimationFrame(frame);
      // Flush the tail: the final scroll before navigating may not have reached its frame yet.
      if (!sawScroll) return;
      try {
        sessionStorage.setItem(key, String(lastTopRef.current));
      } catch {
        // ignore — see above
      }
    };
  }, [pathname]);

  // ── Restore on Back/Forward, reset on a new navigation ──
  useEffect(() => {
    if (navigationType === 'REPLACE') return undefined;

    if (navigationType === 'PUSH') {
      // A deep link (#section) owns its own scrolling — e.g. the announcements page jumps to a post.
      if (hash) return undefined;
      const scroller = getScroller();
      if (scroller) scroller.scrollTop = 0;
      return undefined;
    }

    // POP — back or forward. Re-apply the saved offset until the content is tall enough to hold it.
    const saved = Number(sessionStorage.getItem(storageKey(pathname)) ?? '');
    if (!Number.isFinite(saved) || saved <= 0) return undefined;

    const deadline = performance.now() + RESTORE_DEADLINE_MS;
    let raf = 0;
    const apply = () => {
      const scroller = getScroller();
      // The shell may not have mounted yet on a hard load — keep waiting for it rather than giving up.
      if (scroller) {
        scroller.scrollTop = saved;
        // Reached it: the content was tall enough and we are done.
        if (scroller.scrollTop >= saved - 1) return;
      }
      // Short by more than a pixel means the content has not finished growing yet.
      if (performance.now() < deadline) raf = requestAnimationFrame(apply);
    };
    raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash, navigationType]);
}
